import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { Box, Card, Typography, Chip, CircularProgress, Tooltip } from "@mui/material";
import CommonHeader from "../../CommonComponent/CommonHeader";
import {
  Add,
  // Search as SearchIcon,
  // OpenInNew as OpenInNewIcon,
  Public as PublicIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import ButtonCommon from "../../CommonComponent/ButtonCommon";
import Loader from "../../CommonComponent/Loader";
import { getAllBanners, getBannerById, updateBannerPriority } from "../../api/bannerService";
import { NoBannerLogo } from "../../assets/svg/";
import BannerSetupModal from "./Components/BannerSetupModal";
// import { DEFAULT_WEBSITE_ID, DEFAULT_ORGANIZATION_ID } from '../config/constants';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { showErrorToast, showSuccessToast } from "../../toastUtils";

// Register the English locale
countries.registerLocale(enLocale);

// Get list of countries as { 'US': 'United States', ... }
const countryObj = countries.getNames("en", { select: "official" });

// Convert to array format
const countryList = Object.entries(countryObj).map(([code, name]) => ({
  code,
  name
}));



const BannerDesigner = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]); // Initialize as empty array
  const [activeBanners, setActiveBanners] = useState([]); // Initialize as empty array
  const [inActiveBanners, setInActiveBanners] = useState([]); // Initialize as empty array
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loaderState, setLoaderState] = useState({});
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [isDragLoading, setIsDragLoading] = useState(false);
  const [bannerClicked, setBannerClicked] = useState(false);

  const handleNewBanner = () => {
    setIsSetupModalOpen(true);
  };

  const handleSetupModalClose = () => {
    setIsSetupModalOpen(false);
  };

  // Function to check if a specific banner is the last active one
  const handleBannerClick = async (bannerId) => {
    setBannerClicked(true);
    setLoaderState(prev => ({ ...prev, [bannerId]: { Loader: true } }));
    try {
      // Fetch banner data before navigation
      const response = await getBannerById(bannerId);
      if (response.success) {
        // Get all active banners
        const activeBanners = banners.filter((banner) => banner.is_active);

        // Check if this is the last active banner
        const isLastActiveBanner =
          activeBanners.length === 1 && activeBanners[0].banner_id === bannerId;

        // Add the isLastActiveBanner flag to the data
        const enhancedData = {
          ...response.data,
          isLastActiveBanner: isLastActiveBanner,
        };

        // Store the banner data in localStorage for the edit page
        localStorage.setItem("editBannerData", JSON.stringify(enhancedData));

        // Navigate to edit page
        navigate(`/banner/${bannerId}`);
      }
    } catch (error) {
    } finally {
      // Clear loading state for specific banner
      setLoaderState(prev => ({ ...prev, [bannerId]: { Loader: false } }));
      setBannerClicked(false);
    }
  };

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await getAllBanners();
        // console.log('bannerData', response);

        if (Array.isArray(response)) {
          setBanners(response);
          setError(null);
        } else if (response && Array.isArray(response.data)) {
          setBanners(response.data);
          setError(null);
        } else {
          throw new Error("Invalid data format received");
        }
      } catch (error) {
        console.log(error);
        setError("Failed to load banners");
        setBanners([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length > 0) {
      // Ensure banners is an array before filtering
      const activeBanners = Array.isArray(banners)
        ? banners.filter((banner) => banner.is_active)
        : [];
      setActiveBanners(activeBanners);
      const inactiveBanners = Array.isArray(banners)
        ? banners.filter((banner) => !banner.is_active)
        : [];
      setInActiveBanners(inactiveBanners);
    }
  }, [banners])


  // Function to handle drag and drop
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    // Rearrange banners based on drag result
    const items = Array.from(activeBanners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update priorities in the new list
    const updatedBanners = items.map((banner, index) => ({
      ...banner,
      priority_num: index + 1
    }));

    // Create API payload
    const payload = {
      banners: updatedBanners.map((banner) => ({
        banner_id: banner.banner_id,
        priority_num: banner.priority_num
      }))
    }

    try {
      // Optional: show loading state
      setIsDragLoading(true);

      const response = await updateBannerPriority(payload);

      if (response?.success) {
        setActiveBanners(updatedBanners); // update local state
        showSuccessToast("Priority updated successfully");
      } else {
        throw new Error(response?.message || "Unknown error");
      }
    } catch (error) {
      console.error("Failed to update priority:", error);
      showErrorToast(error.message || "Failed to update priority");
    } finally {
      setIsDragLoading(false); // if you're using a loading state
    }
  };



  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        {error}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* Header name of banner */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <CommonHeader
              title="Consent Banners"
              subTitle="Update default banner, add new banner or delete banner on this screen."
            />
          </Box>
          <Box sx={{ display: "flex" }}>
            <ButtonCommon size="small" icon={<Add />} onClick={handleNewBanner}>
              New Banner
            </ButtonCommon>
          </Box>
        </Box>

        {/* Banner Setup Modal */}
        <BannerSetupModal
          open={isSetupModalOpen}
          onClose={handleSetupModalClose}
          activeBanners={activeBanners}
        // onSubmit={handleSetupModalSubmit}
        />

        {/* Banner List Section */}

        {banners.length === 0 ? (
          // Empty state with centered content
          <Card
            sx={{
              p: 6,
              height: "calc(100vh - 200px)",
              borderRadius: 1,
              background: "#f8f9fa",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
            variant="outlined"
          >
            <NoBannerLogo
              sx={{
                width: "200px",
                height: "auto",
                mb: 3,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#1a1a1a",
                mb: 2,
              }}
            >
              Nothing To see Here Yet
            </Typography>
            <Typography
              sx={{
                color: "#666",
                mb: 4,
                maxWidth: "500px",
              }}
            >
              There is no banner configured yet. Please add your first banner.
            </Typography>
            <ButtonCommon
              size="large"
              icon={<Add />}
              onClick={handleNewBanner}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1rem",
              }}
            >
              New Banner
            </ButtonCommon>
          </Card>
        ) : (
          <Box>
            {/* Active Banners Section */}
            <Card
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 1,
                background: "#f8f9fa",
                cursor: "pointer",
              }}
              variant="outlined"
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#525455", mb: 1 }}
              >
                Active Banners
              </Typography>
              <Typography sx={{ color: "#666", fontSize: "0.875rem", mb: 3 }}>
                If there are multiple banners target the same region, the banner
                with the highest priority (in order below) will be shown.
              </Typography>

              {isDragLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                  }}
                >
                  <Loader />
                </Box>) :
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="banners">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {activeBanners
                          .filter((banner) => banner.is_active)
                          .sort((a, b) => a.priority_num - b.priority_num)
                          .map((banner, index) => (
                            <Draggable
                              key={banner.banner_id}
                              draggableId={banner.banner_id.toString()}
                              index={index}
                              isDragDisabled={bannerClicked}
                            >
                              {(provided, snapshot) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    py: 2,
                                    px: 2,
                                    mb: 2,
                                    backgroundColor: "#fff",
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: 'center',
                                    // justifyContent: 'space-between',
                                    gap: 2,
                                    // Add drag animation styles
                                    transition: 'background-color 0.2s ease',
                                    ...(snapshot.isDragging && {
                                      backgroundColor: '#f5f5f5',
                                      boxShadow: '0 5px 10px rgba(0,0,0,0.1)'
                                    })
                                  }}
                                  onClick={() => handleBannerClick(banner.banner_id)}
                                >
                                  <Box {...provided.dragHandleProps}>
                                    <DragIndicatorIcon
                                      sx={{
                                        cursor: 'grab',
                                        color: '#666',
                                        '&:hover': { color: !bannerClicked && '#00B894' }
                                      }}
                                    />
                                  </Box>

                                  <Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "100%",
                                      }}
                                    >
                                      {/* Left side content */}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            color: "#525455",
                                            fontWeight: 700,
                                          }}
                                        >
                                          {banner.banner_name}
                                        </Typography>
                                        {/* <Chip
            label={banner.type}
            sx={{
              backgroundColor: banner.type.includes("Explicit")
                ? "#e3f2fd"
                : "#f5f5f5",
              color: banner.type.includes("Explicit")
                ? "#0046fc"
                : "#666",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          /> */}
                                      </Box>
                                      {/* Right side icons */}
                                      {/* <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <SearchIcon
            sx={{
              color: "#666",
              cursor: "pointer",
              "&:hover": { color: "#0046fc" },
            }}
          />
          <OpenInNewIcon
            sx={{
              color: "#666",
              cursor: "pointer",
              "&:hover": { color: "#0046fc" },
            }}
          />
        </Box> */}
                                      {loaderState[banner.banner_id]?.Loader && <CircularProgress size={30} />}
                                    </Box>

                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Box
                                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                      >
                                        <PublicIcon sx={{ fontSize: "1rem", color: "#666" }} />
                                        {/* <Typography
                sx={{ color: "#666", fontSize: "0.875rem" }}
              >
                {banner.banner_country_list?.length > 0 ?
                  banner.banner_country_list.map((country, index) => (
                    <span key={index}>
                      {countryList.find(c => c.code === country)?.name}
                      {index < banner.banner_country_list.length - 1 && ", "}
                    </span>
                  )) :
                  "Worldwide"
                }
              </Typography> */}
                                        <Tooltip
                                          title={
                                            banner.target_type === "WorldWide" ? "WorldWide" :
                                              banner.banner_country_list?.length > 0 && banner.banner_country_list
                                                .map(code => countryList.find(c => c.code === code)?.name)
                                                .filter(Boolean)
                                                .join(", ")
                                          }
                                        >
                                          <Typography
                                            sx={{
                                              color: "#666",
                                              fontSize: "0.875rem",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              maxWidth: "200px", // Adjust width as needed
                                              display: 'block', // Required for ellipsis to work in Typography
                                              cursor: "default"
                                            }}
                                          >
                                            {banner.target_type === "WorldWide" ? "WorldWide" :
                                              banner.banner_country_list?.length > 0 && banner.banner_country_list
                                                .map(code => countryList.find(c => c.code === code)?.name)
                                                .filter(Boolean)
                                                .join(", ")
                                            }
                                          </Typography>
                                        </Tooltip>

                                      </Box>
                                      {banner.framework_name && (
                                        <Box
                                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                        >
                                          <DescriptionIcon sx={{ fontSize: "1rem", color: "#666" }} />
                                          <Typography
                                            sx={{ color: "#666", fontSize: "0.875rem" }}
                                          >
                                            {banner.framework_name}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Box
                                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                      >
                                        <CheckCircleIcon
                                          sx={{ fontSize: "1rem", color: "#2e7d32" }}
                                        />
                                        <Chip
                                          label="Active"
                                          sx={{
                                            backgroundColor: "#e8f5e9",
                                            color: "#2e7d32",
                                            fontWeight: 500,
                                            fontSize: "0.75rem",
                                            height: "24px",
                                          }}
                                        />
                                      </Box>
                                      <Chip
                                        // label={`Priority #${index + 1}`} // Priority based on sorted index
                                        label={`Priority #${banner.priority_num}`}
                                        sx={{
                                          backgroundColor: "#f5f5f5",
                                          color: "#666",
                                          fontWeight: 500,
                                          fontSize: "0.75rem",
                                          height: "24px",
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              }

            </Card>

            {/* Inactive Banners Section */}
            <Card
              sx={{
                p: 3,
                height: "100%",
                borderRadius: 1,
                background: "#f8f9fa",
                mt: 4,
              }}
              variant="outlined"
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#525455", mb: 1 }}
              >
                Inactive Banners
              </Typography>
              <Typography sx={{ color: "#666", fontSize: "0.875rem", mb: 3 }}>
                Inactive services are not displayed in the consent banner. has
                context menu
              </Typography>

              {/* {inactiveBanners */}
              {inActiveBanners
                .filter((banner) => !banner.is_active)
                .map((banner, index) => (

                  <Box
                    key={index}
                    onClick={() => handleBannerClick(banner.banner_id)}
                    sx={{
                      py: 2,
                      px: 2,
                      mb: 2,
                      backgroundColor: "#fff",
                      borderRadius: 2,
                    }}
                  >

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      {/* Left side content */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#525455",
                            fontWeight: 700,
                          }}
                        >
                          {banner.banner_name}
                        </Typography>
                        {/* <Chip
                          label={banner.type}
                          sx={{
                            backgroundColor: banner.type.includes("Explicit")
                              ? "#e3f2fd"
                              : "#f5f5f5",
                            color: banner.type.includes("Explicit")
                              ? "#0046fc"
                              : "#666",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            height: "24px",
                          }}
                        /> */}
                      </Box>
                      {/* Right side icons */}
                      {/* <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <SearchIcon
                          sx={{
                            color: "#666",
                            cursor: "pointer",
                            "&:hover": { color: "#0046fc" },
                          }}
                        />
                        <OpenInNewIcon
                          sx={{
                            color: "#666",
                            cursor: "pointer",
                            "&:hover": { color: "#0046fc" },
                          }}
                        />
                      </Box> */}
                      {loaderState[banner.banner_id]?.Loader && <CircularProgress size={30} />}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <PublicIcon sx={{ fontSize: "1rem", color: "#666" }} />
                        <Typography
                          sx={{ color: "#666", fontSize: "0.875rem" }}
                        >
                          {banner.target_type}
                        </Typography>
                      </Box>
                      {banner.framework_name && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <DescriptionIcon sx={{ fontSize: "1rem", color: "#666" }} />
                          <Typography
                            sx={{ color: "#666", fontSize: "0.875rem" }}
                          >
                            {banner.framework_name}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CancelIcon
                          sx={{ fontSize: "1rem", color: "#d32f2f" }}
                        />
                        <Chip
                          label="Inactive"
                          sx={{
                            backgroundColor: "#f5f5f5",
                            color: "#666",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            height: "24px",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                ))}
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BannerDesigner;


